import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { connectWS, onWS } from "@/lib/chatws";
import api from "@/lib/api";
import { setAppBadge } from "@/lib/push";

const ChatNotifyContext = createContext({ unread: 0 });
export const useChatNotify = () => useContext(ChatNotifyContext);

// Soft two-tone chime using the Web Audio API (no asset file needed).
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const notes = [
      { f: 880, t: 0 },
      { f: 1174.66, t: 0.12 },
    ];
    notes.forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.18, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.4);
    });
    setTimeout(() => ctx.close().catch(() => {}), 900);
  } catch (e) { /* ignore */ }
}

export function ChatNotifyProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadIds, setUnreadIds] = useState(new Set());
  const pathRef = useRef(location.pathname);
  const baseTitleRef = useRef(document.title);
  const titleTimerRef = useRef(null);

  const refreshBadge = () => api.get("/chat/unread-count").then((r) => setAppBadge(r.data.count)).catch(() => {});

  useEffect(() => {
    pathRef.current = location.pathname;
    if (location.pathname.startsWith("/chat")) {
      setUnreadIds((prev) => (prev.size ? new Set() : prev));
    }
    // Reading messages updates read_at server-side; give it a moment then re-sync the badge.
    const t = setTimeout(refreshBadge, 700);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Flash the browser tab title while there are unread messages.
  useEffect(() => {
    clearInterval(titleTimerRef.current);
    if (unreadIds.size === 0) {
      document.title = baseTitleRef.current;
      return;
    }
    const label = `(${unreadIds.size}) New message${unreadIds.size > 1 ? "s" : ""}`;
    let flip = false;
    document.title = label;
    titleTimerRef.current = setInterval(() => {
      flip = !flip;
      document.title = flip ? baseTitleRef.current : label;
    }, 1200);
    return () => clearInterval(titleTimerRef.current);
  }, [unreadIds]);

  useEffect(() => {
    if (!user || user.must_change_password) {
      setAppBadge(0);
      return;
    }
    connectWS();
    refreshBadge();
    const off = onWS((data) => {
      if (data.type !== "message") return;
      // A new message arrived — re-sync the icon badge from the server.
      refreshBadge();
      // The chat page handles its own display; only notify from elsewhere.
      if (pathRef.current.startsWith("/chat")) return;

      const isChannel = data.conversation_type === "channel";
      const title = isChannel ? `${data.sender_name} in ${data.conversation_name}` : data.sender_name;
      const preview = data.message?.text?.trim() || (data.message?.attachment ? "Sent an attachment" : "New message");

      setUnreadIds((prev) => new Set(prev).add(data.conversation_id));
      playChime();
      toast(title, {
        description: preview.length > 90 ? preview.slice(0, 90) + "…" : preview,
        action: { label: "Open", onClick: () => navigate("/chat") },
        duration: 6000,
      });
    });
    return off;
  }, [user, navigate]);

  return (
    <ChatNotifyContext.Provider value={{ unread: unreadIds.size }}>
      {children}
    </ChatNotifyContext.Provider>
  );
}
